/**
 * Firebase integration - the only place referencing Firebase package
 *
 * Main rules:
 * Only use store and Firebase package here.
 * Avoid everything expect Firebase, even store references.
 */

import Firebase from "@firebase/app";
import store from "@/store/index";
import options from "@/options";

require("@firebase/auth");
require("@firebase/database");

// initialization /////////////////////////////////////////////////////////////
const firebase = Firebase.initializeApp(options.firebaseConfig);

// watchers ///////////////////////////////////////////////////////////////////
function startFirebaseSync(userId) {
  firebase
    .database()
    .ref(`users/${userId}/settings/currency`)
    .on("value", (snapshot) => {
      store.dispatch("settings/setCurrency", {
        source: "firebase",
        currency: snapshot.val() || options.defaultCurrency,
      });
    });

  firebase
    .database()
    .ref(`users/${userId}/assets`)
    .on("value", (snapshot) => {
      const assets = snapshot.val() || null;
      store.dispatch("portfolio/setAssets", {
        assets,
        source: "firebase",
      });
    });

  firebase
    .database()
    .ref(`users/${userId}/profitloss/assets`)
    .on("value", (snapshot) => {
      const assets = snapshot.val() || null;
      store.dispatch("profitloss/setAssets", {
        assets,
        source: "firebase",
      });
    });

  firebase
    .database()
    .ref(`users/${userId}/profitloss/transactions`)
    .on("value", (snapshot) => {
      const transactions = snapshot.val() || null;
      store.dispatch("profitloss/setTransactions", {
        transactions,
        source: "firebase",
      });
    });
}

function stopFirebaseSync(userId) {
  firebase
    .database()
    .ref(`users/${userId}/settings/currency`)
    .off();
  firebase
    .database()
    .ref(`users/${userId}/assets`)
    .off();
  firebase
    .database()
    .ref(`users/${userId}/profitloss/assets`)
    .off();
  firebase
    .database()
    .ref(`users/${userId}/profitloss/transactions`)
    .off();
}

// watch firebase's internal auth state and update it to store
firebase.auth().onAuthStateChanged(async (user) => {
  if (user) {
    store.dispatch("auth/onUpdateAuth", { user });
    startFirebaseSync(user.uid);
  } else if (store.state.auth.user) {
    stopFirebaseSync(store.state.auth.user.uid);
    store.dispatch("auth/onRemoveAuth");
  }
});

// internal functions /////////////////////////////////////////////////////////
function signIn(provider) {
  // TODO: check this, for phone applications?
  if (navigator.userAgent.match(/CoinTrackr App/) !== null) {
    return firebase.auth().signInWithRedirect(provider);
  }
  return firebase.auth().signInWithPopup(provider);
}

// exports ////////////////////////////////////////////////////////////////////
export default {
  getInstance() {
    return firebase;
  },
  user() {
    return firebase.auth().user;
  },
  getIdToken(forceRefresh = true) {
    return firebase.auth().currentUser.getIdToken(forceRefresh);
  },
  register(email, password) {
    try {
      return firebase.auth().createUserWithEmailAndPassword(email, password);
    } catch (error) {
      console.log("FIREBASE register ERROR ", error);
    }
  },
  login(type) {
    switch (type) {
      case "facebook":
        return () => signIn(new Firebase.auth.FacebookAuthProvider());
      case "twitter":
        return () => signIn(new Firebase.auth.TwitterAuthProvider());
      case "google":
        return () => signIn(new Firebase.auth.GoogleAuthProvider());
      default:
        try {
          return (email, password) =>
            firebase.auth().signInWithEmailAndPassword(email, password);
        } catch (error) {
          console.log("FIREBASE login ERROR", error);
        }
    }
  },
  signOut() {
    return firebase.auth().signOut();
  },
  resetPassword(email) {
    return firebase.auth().sendPasswordResetEmail(email);
  },

  // Share functionality //////////////////////////////////////////////////////
  getShareLink(id, payload) {
    return firebase
      .database()
      .ref(`shares/${id}`)
      .set(payload);
  },
  getSharedData(id) {
    return firebase
      .database()
      .ref(`/shares/${id}`)
      .once("value")
      .then((snapshot) => snapshot.val());
  },

  // save data ////////////////////////////////////////////////////////////////
  saveCurrency(currency) {
    const userId = firebase.auth().currentUser.uid;
    return firebase
      .database()
      .ref(`users/${userId}/settings/currency`)
      .set(currency);
  },
  saveAssets(assets) {
    const userId = firebase.auth().currentUser.uid;
    return firebase
      .database()
      .ref(`users/${userId}/assets`)
      .set(assets);
  },
  saveProfitlossAssets(profitlossAssets) {
    const userId = firebase.auth().currentUser.uid;
    return firebase
      .database()
      .ref(`users/${userId}/profitloss/assets`)
      .set(profitlossAssets);
  },
  saveProfitlossTransactions(profitlossTransactions) {
    const userId = firebase.auth().currentUser.uid;
    return firebase
      .database()
      .ref(`users/${userId}/profitloss/transactions`)
      .set(profitlossTransactions);
  },
};
